<template>
    <div class="flex flex-col lg:flex-row justify-between items-center">
        <h2 class="text-xl leading-9 font-headline font-medium text-bhav-blue-600">Videos</h2>
        <FormButton v-if="GlobalPolicy.edit()" color="blue" type="button" @click="() => {
            modal = true
        }">
            <span class="fak fa-plus mr-1"></span>Add
        </FormButton>
    </div>
    <PaginationTable empty-message="There are currently no video items." :data="item.meta.videos" :columns="columns" :view="`${$route.name}-${$route.params.tab}`" :legacy="false">
        <template #col-position="{ row }">
            <div class="flex gap-2 items-center py-4">
                <div class="text-sm leading-5 text-oakwood-gray-900 mr-2">{{ item.meta.videos.indexOf(row) + 1 }}</div>
                <button type="button" :disabled="item.meta.videos.indexOf(row) == 0" @click="move(row, 'up')" :class="{ 'cursor-pointer': item.meta.videos.indexOf(row) > 0, 'text-oakwood-gray-300 cursor-not-allowed': item.meta.videos.indexOf(row) == 0 }">
                    <span class="far fa-arrow-up"></span>
                </button>
                <button type="button" :disabled="item.meta.videos.indexOf(row) == item.meta.videos.length - 1" @click="move(row, 'down')" :class="item.meta.videos.indexOf(row) < item.meta.videos.length - 1 ? 'cursor-pointer' : 'text-oakwood-gray-300 cursor-not-allowed'">
                    <span class="far fa-arrow-down"></span>
                </button>
            </div>
        </template>

        <template #col-title="{ row }">
            <a :href="row.url" target="_blank" class="block py-4 text-bhav-blue-600 hover:text-bhav-blue-900">
                {{ row.title }}
            </a>
        </template>

        <template #col-type="{ row }">
            <div class="py-4">
                {{ row.type }}
            </div>
        </template>

        <template #col-actions="{ row }" v-if="GlobalPolicy.edit()">
            <div class="flex gap-2 justify-end py-4">
                <FormButton type="button" @click="edit(item.meta.videos.indexOf(row))" prefix-class="fak" icon="fa-pen" color="blue">
                    Edit
                </FormButton>
                <FormButton type="button" @click="openDelete(item.meta.videos.indexOf(row))" prefix-class="fak" icon="fa-trash" color="red" />
            </div>
        </template>
    </PaginationTable>

    <ModalDialog :open="modal" :no-padding="true" @close="cancel">
        <template #header>
            <div>
                <h3 class="text-lg leading-6 font-medium text-bhav-blue-500">
                    Video Editor
                </h3>
                <p class="mt-1 max-w-2xl text-sm leading-5 text-oakwood-gray-500">
                    Enter the details about a video you wish to include with this {{ context }}.
                </p>
            </div>
        </template>
        <template #body>
            <div class="mt-6 sm:mt-5">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t w-full sm:border-oakwood-gray-200 sm:py-5">
                    <label for="title" class="block text-sm font-medium leading-5 text-bhav-blue-500 sm:mt-px sm:pt-2">
                        Title
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <TextInput id="title" v-model="video.title" :max-length="50" />
                    </div>
                </div>

                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="url" class="block text-sm font-medium leading-5 text-bhav-blue-500 sm:mt-px sm:pt-2">
                        URL
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <URLField id="url" v-model="video.url" width="w-full" />
                    </div>
                </div>

                <div v-if="false" class="mt-6 sm:mt-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:pt-5">
                    <label for="type" class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        Type
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <div class="max-w-lg rounded-md shadow-sm sm:max-w-xs">
                            <select id="type" v-model="video.type" class="block p-2 bg-white border border-oakwood-gray-300 rounded w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5">
                                <option value="URL">URL</option>
                                <option value="Filename">Filename</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #buttons>
            <div class="flex justify-end mt-5 pt-5">
                <span class="inline-flex rounded-md shadow-sm">
                    <FormButton type="button" @click="cancel" color="gray">
                        Cancel
                    </FormButton>
                </span>
                <span class="ml-3 inline-flex rounded-md shadow-sm">
                    <FormButton type="button" @click="save" color="blue">
                        Save
                    </FormButton>
                </span>
            </div>
        </template>
    </ModalDialog>

    <ModalDialog v-if="deleting" :no-padding="true" :open="deleting" @close="cancel">
        <template #header>
            <h2 class="text-lg text-bhav-blue-500 font-semibold">Confirm Video Deletion</h2>
        </template>
        <template #body>
            <p class="text-oakwood-gray-900"><strong>Warning!</strong> You are about to delete <em>{{ item.meta.videos[index]?.title }}</em>. Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="drop" color="blue">
                Delete
            </FormButton>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
        </template>
    </ModalDialog>
</template>

<script>
import ModalDialog from '@/components/ModalDialog.vue';
import { FormButton, URLField } from '@/components/form';
import { GlobalPolicy } from '@/policies/global';
import PaginationTable from '@/components/PaginationTable.vue';
import TextInput from '../form/TextInput.vue';

function defaultVideo() {
    return {
        type: 'URL',
        title: '',
        url: ''
    }
}

export default {
    setup() {
        return {
            GlobalPolicy
        }
    },
    data() {
        return {
            modal: false,
            index: false,
            video: defaultVideo(),
            deleting: false
        }
    },
    emits: [
        'update'
    ],
    computed: {
        columns() {
            let cols = [
                {
                    name: 'position',
                    label: 'Position',
                    sort: false,
                    default: true
                },
                {
                    name: 'title',
                    label: 'Title',
                    sort: false,
                    default: true
                },
                {
                    name: 'type',
                    label: 'Type',
                    sort: false,
                    default: true
                }
            ];
            if (GlobalPolicy.edit()) {
                cols.push({
                    name: 'actions',
                    label: '',
                    sort: false,
                    default: true,
                    class: 'text-right'
                });
            }
            return cols;
        }
    },
    methods: {
        move(video, direction) {
            const item = this.item;
            let index = item.meta.videos.indexOf(video),
                to = direction == 'up' ? index - 1 : index + 2,
                from = direction == 'up' ? index + 1 : index;
            item.meta.videos.splice(to, 0, video)
            item.meta.videos.splice(from, 1);
            this.$emit('update', item);
        },
        validate() {
            if (this.video.title == '' || this.video.url == '' || this.video.type == '') {
                console.error('Missing required values');
                return false;
            }
            return true;
        },
        edit(index) {
            this.modal = 1;
            this.index = index;
            this.video = { ...this.item.meta.videos[index] };
        },
        save() {
            if (!this.validate()) return;
            const item = this.item;
            if (this.index === false) {
                item.meta.videos.push({ ...this.video });
            } else {
                item.meta.videos.splice(this.index, 1, { ...this.video });
            }
            this.$emit('update', item);
            this.cancel();
        },
        drop() {
            if (this.index === false) {
                console.error('You must select an item to delete');
                return;
            }
            const item = this.item;
            item.meta.videos.splice(this.index, 1);
            this.$emit('update', item);
            this.cancel();
        },
        openDelete(i) {
            this.index = i;
            this.deleting = true;
        },
        cancel() {
            this.video = defaultVideo();
            this.index = false;
            this.modal = false;
            this.deleting = false;
        }
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        context: {
            type: String,
            required: true,
        }
    },
    components: {
        FormButton, ModalDialog, URLField, PaginationTable, TextInput
    }
}
</script>